import React, { useState, useCallback, DragEvent } from "react";
import { useDispatch } from "react-redux";
import ReactFlow, {
  NodeChange,
  EdgeChange,
  Connection,
  HandleType,
  ReactFlowInstance,
} from "reactflow";
import { blockConfigs } from "../../models/block";
import { COLOR_PRIMARY_DARK, COLOR_PRIMARY_LIGHT } from "../../styles/colors";
import { addBlock } from "../../store/game/gameSlice";
import { useAppSelector } from "../../hooks";
import {
  nodesChanged,
  edgesChanged,
  onConnect,
  nodeHandleClick,
  connectEnd,
  setDraggingNode,
} from "../../store/game/gameSlice";
import { selectNodes, selectEdges } from "../../store/game/selectors";
import ReactFlowBlock from "../block/ReactFlowBlock";
import ReactFlowEdge from "../block/ReactFlowEdge";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";

const nodeTypes = { gameBlock: ReactFlowBlock };
const edgeTypes = { gameEdge: ReactFlowEdge };

const BlockCanvas: React.FC = () => {
  const dispatch = useDispatch();
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance?.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (!position) return;

      const blockConfig = blockConfigs[type as keyof typeof blockConfigs];
      if (!blockConfig) return;

      const { inputCapacity, processingCapacity } = blockConfig;

      dispatch(
        addBlock({
          top: position.y,
          left: position.x,
          id: "temp",
          width: 100,
          height: 100,
          blockType: type as any,
          inputCapacity,
          processingCapacity,
        })
      );
      trackEvent(GA_EVENTS.BLOCK_ADDED, { block_type: type });
    },
    [reactFlowInstance, dispatch]
  );

  const nodes = useAppSelector((state) => selectNodes(state.game));
  const edges = useAppSelector((state) => selectEdges(state.game));

  const handleNodesChanged = (changes: NodeChange[]) => {
    dispatch(nodesChanged(changes));
    const removed = changes.filter((c) => c.type === "remove");
    if (removed.length > 0) {
      trackEvent(GA_EVENTS.BLOCK_DELETED, { count: removed.length });
    }
  };

  const handleEdgesChanged = (changes: EdgeChange[]) => {
    dispatch(edgesChanged(changes));
    const removed = changes.filter((c) => c.type === "remove");
    if (removed.length > 0) {
      trackEvent(GA_EVENTS.CONNECTION_DELETED, { count: removed.length });
    }
  };

  const handleConnect = (connection: Connection) => {
    dispatch(onConnect(connection));
    trackEvent(GA_EVENTS.CONNECTION_ADDED);
  };

  const handleConnectStart = (
    event: any,
    params: {
      nodeId: string | null;
      handleId: string | null;
      handleType: HandleType | null;
    }
  ) => {
    dispatch(
      nodeHandleClick({
        nodeID: params.nodeId || "",
        type: params.handleType || "",
      })
    );
  };

  const handleConnectEnd = () => {
    dispatch(connectEnd());
  };

  const handleNodeDragStart = useCallback(() => {
    dispatch(setDraggingNode(true));
  }, [dispatch]);

  const handleNodeDragStop = useCallback(() => {
    dispatch(setDraggingNode(false));
  }, [dispatch]);

  const handleFitView = useCallback(() => {
    reactFlowInstance?.fitView({ padding: 0.15, duration: 500 });
  }, [reactFlowInstance]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChanged}
        onEdgesChange={handleEdgesChanged}
        onConnect={handleConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onConnectStart={handleConnectStart}
        onConnectEnd={handleConnectEnd}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        onInit={setReactFlowInstance}
        onDrop={onDrop}
        onDragOver={onDragOver}
        deleteKeyCode={""}
        defaultEdgeOptions={{ style: { strokeWidth: 3, stroke: "#6b7280" } }}
        connectionLineStyle={{ strokeWidth: 3, stroke: "#6b7280" }}
      />
      <button
        id="fit-view-btn"
        onClick={handleFitView}
        title="Fit all blocks in view"
        style={{
          position: "absolute",
          bottom: "24px",
          right: "24px",
          zIndex: 10,
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          border: "none",
          cursor: "pointer",
          background: `linear-gradient(135deg, ${COLOR_PRIMARY_DARK} 0%, ${COLOR_PRIMARY_LIGHT} 100%)`,
          boxShadow: `0 4px 20px rgba(21, 118, 152, 0.55), 0 2px 8px rgba(0,0,0,0.2)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          color: "#fff",
          fontSize: "20px",
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.12)";
          (e.currentTarget as HTMLButtonElement).style.boxShadow =
            "0 6px 28px rgba(21, 118, 152, 0.75), 0 4px 12px rgba(0,0,0,0.25)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
          (e.currentTarget as HTMLButtonElement).style.boxShadow =
            "0 4px 20px rgba(21, 118, 152, 0.55), 0 2px 8px rgba(0,0,0,0.2)";
        }}
        onMouseDown={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.96)";
        }}
        onMouseUp={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.12)";
        }}
      >
        {/* Crosshair / fit-view icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 7V5a2 2 0 0 1 2-2h2" />
          <path d="M17 3h2a2 2 0 0 1 2 2v2" />
          <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
    </div>
  );
};

export default BlockCanvas;
